// useRoutineGenerator.js
// Location: src/hooks/useRoutineGenerator.js
// Custom hook for generating daily practice routines with smart rotation

import { useState, useEffect, useCallback } from 'react';
import { warmups, grooves, getCurrentSong, getActiveSongs } from '../data/DrumData.v2';

/**
 * Custom hook to generate and manage today's practice routine
 * Implements smart rotation algorithm that respects pins and excludes
 */
export const useRoutineGenerator = (songs, pinnedItems, excludedItems) => {
  const [todaysRoutine, setTodaysRoutine] = useState(() => {
    const saved = localStorage.getItem('drumflow_todays_routine');
    if (saved) {
      const parsed = JSON.parse(saved);
      const today = new Date().toDateString();
      if (parsed.date === today) {
        return parsed.routine;
      }
    }
    return null;
  });

  /**
   * Generate a new routine for today
   */
  const generateRoutine = useCallback(() => {
    console.log('🔄 Generating new routine...'); // Debug log
    
    // Helper to select random item (instead of day-based)
    const selectRandomItem = (items) => {
      const available = items.filter(item => !excludedItems.includes(item.id));
      if (available.length === 0) return items[0];
      
      const pinned = available.filter(item => pinnedItems.includes(item.id));
      
      if (pinned.length === 0) {
        // No pins: select random from available
        return available[Math.floor(Math.random() * available.length)];
      } else if (pinned.length === 1) {
        // Exactly 1 pin: always use it
        return pinned[0];
      } else {
        // Multiple pins: select random from pinned
        return pinned[Math.floor(Math.random() * pinned.length)];
      }
    };
    
    // Select warmup using random selection
    const warmup = selectRandomItem(warmups);
    
    // Select groove/skill using random selection
    const groove = selectRandomItem(grooves);
    
    // For song: use current song or first active song as fallback
    const currentSong = getCurrentSong(songs);
    const activeSongs = getActiveSongs(songs);
    const song = currentSong || activeSongs[0];
    
    // Build routine object with timestamp to force re-render
    const routine = {
      warmup,
      groove,
      song,
      _generatedAt: Date.now() // Forces React to see it as new
    };
    
    console.log('✅ New routine generated:', routine); // Debug log
    
    // Save to localStorage with today's date
    const today = new Date().toDateString();
    localStorage.setItem('drumflow_todays_routine', JSON.stringify({
      date: today,
      routine
    }));
    
    setTodaysRoutine(routine);
    
    return routine;
  }, [songs, pinnedItems, excludedItems]);

  // Auto-generate routine on mount if none exists
  useEffect(() => {
    if (!todaysRoutine) {
      console.log('🆕 Initial routine generation');
      generateRoutine();
    }
  }, [todaysRoutine, generateRoutine]);

  return {
    routine: todaysRoutine,
    generateRoutine
  };
};